import { useState, useEffect } from "react";
import axios from "axios";
import {
  DollarSign, Copy, Users, Clock, ChevronDown, ChevronUp,
  Loader2, QrCode, Download, Mail, RefreshCw, Star, ToggleLeft, ToggleRight,
  Trash2, Play, Pause, CheckCircle2, XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

const EarnPage = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.email === "ai.manifest.official@gmail.com";
  return (
    <DashboardLayout currentPage="earn">
      <div className="p-6 max-w-6xl mx-auto space-y-8">
        {isAdmin ? <AdminEarnView token={token} user={user} /> : <UserEarnView user={user} token={token} />}
      </div>
    </DashboardLayout>
  );
};

/* ═══════════ QR MODAL — shared by admin + affiliates ═══════════ */
const QrModal = ({ qrUrl, onClose }) => {
  if (!qrUrl) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="bg-aura-surface rounded-2xl p-6 border border-white/10 max-w-md w-full mx-4 text-center" onClick={e => e.stopPropagation()}>
        <p className="text-white font-medium mb-4">Affiliate QR Code</p>
        <div className="bg-[#050A0A] rounded-xl p-2 mb-4 inline-block"><img src={qrUrl} alt="QR Code" className="w-full max-w-[360px] mx-auto" /></div>
        <div className="flex gap-2 justify-center">
          <a href={qrUrl} download="aura_affiliate_qr.png"><Button size="sm"><Download className="w-3 h-3 mr-1" /> Download</Button></a>
          <Button size="sm" variant="outline" className="border-white/10" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

const useQr = (token) => {
  const [qrUrl, setQrUrl] = useState(null);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const showQr = async (userId) => {
    try {
      const res = await axios.get(`${API}/earn/affiliate/${userId}/qr`, { headers, responseType: "blob" });
      setQrUrl(URL.createObjectURL(res.data));
    } catch { toast.error("Failed to load QR code"); }
  };
  const closeQr = () => { if (qrUrl) URL.revokeObjectURL(qrUrl); setQrUrl(null); };
  return { qrUrl, showQr, closeQr };
};

/* ═══════════ ADMIN EARN VIEW ═══════════ */
const AdminEarnView = ({ token, user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [expandedId, setExpandedId] = useState(null);
  const [instantApproval, setInstantApproval] = useState(false);
  const { qrUrl, showQr, closeQr } = useQr(token);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = async () => {
    try {
      const [res, sRes] = await Promise.all([
        axios.get(`${API}/earn/admin/earn-overview`, { headers }),
        axios.get(`${API}/earn/admin/settings/instant-approval`, { headers }).catch(() => null)
      ]);
      setData(res.data);
      if (sRes) setInstantApproval(sRes.data.enabled);
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, []);

  const seedHouse = async () => { try { await axios.post(`${API}/earn/admin/house-affiliate/seed`, {}, { headers }); toast.success("House affiliate ready"); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const toggleInstant = async () => { try { const v = !instantApproval; await axios.post(`${API}/earn/admin/settings/instant-approval`, { enabled: v }, { headers }); setInstantApproval(v); toast.success(v ? "Instant approval ON" : "Instant approval OFF"); } catch { toast.error("Failed"); } };
  const doAction = async (uid, action) => { try { await axios.post(`${API}/earn/admin/affiliates/${uid}/${action}`, {}, { headers }); toast.success(`${action} done`); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const markMonthPaid = async (uid, month) => { try { await axios.post(`${API}/earn/admin/affiliates/${uid}/mark-month-paid`, { month }, { headers }); toast.success(`${month} paid`); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied!"); };

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-aura-emerald" /></div>;

  const s = data?.summary || {};
  const affs = data?.affiliates || [];
  const house = affs.find(a => a.is_house);
  const pending = affs.filter(a => a.status === "pending_review");
  const retail = affs.filter(a => a.status === "approved" && a.affiliate_type === "retail" && !a.is_house);
  const online = affs.filter(a => a.status === "approved" && a.affiliate_type !== "retail" && !a.is_house);
  const paused = affs.filter(a => a.status === "paused");

  return (
    <>
      <QrModal qrUrl={qrUrl} onClose={closeQr} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white mb-1">Affiliate Command Center</h1>
          <p className="text-aura-text-secondary text-sm">Manage affiliates, referrals, and payouts.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={toggleInstant} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-aura-emerald/30 transition-colors" data-testid="instant-approval-toggle">
            {instantApproval ? <ToggleRight className="w-5 h-5 text-aura-emerald" /> : <ToggleLeft className="w-5 h-5 text-aura-text-muted" />}
            <span className={`text-xs font-medium ${instantApproval ? "text-aura-emerald" : "text-aura-text-muted"}`}>Instant Approval</span>
          </button>
          {!house && <Button size="sm" className="bg-aura-emerald/20 text-aura-emerald text-xs" onClick={seedHouse}>Create House Affiliate</Button>}
          <Button size="sm" variant="outline" className="border-white/10 text-aura-text-muted text-xs" onClick={fetchData}><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SC label="Total Applications" value={s.total_applications || 0} />
        <SC label="Pending" value={s.pending_approvals || 0} accent="yellow" />
        <SC label="Retail" value={s.approved_retail || 0} accent="blue" />
        <SC label="Online" value={s.approved_online || 0} accent="purple" />
        <SC label="Referred Subs" value={s.total_referred_subscribers || 0} accent="emerald" />
        <SC label="Monthly Due" value={`$${(s.monthly_payout_due || 0).toFixed(2)}`} accent="gold" />
        <SC label="Attributed Rev" value={`$${(s.monthly_attributed_revenue || 0).toFixed(2)}`} accent="emerald" />
        <SC label="House Refs" value={house?.total_referrals || 0} />
      </div>

      {/* House Affiliate — pinned at top, internal, no payout, with expandable Users dropdown */}
      {house && <HouseAffiliateCard house={house} copyText={copyText} showQr={showQr} />}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2 flex-wrap">
        {[{ id: "pending", label: `Pending (${pending.length})` }, { id: "retail", label: `Retail (${retail.length})` }, { id: "online", label: `Online (${online.length})` }, { id: "paused", label: `Paused (${paused.length})` }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-aura-emerald/10 text-aura-emerald border-b-2 border-aura-emerald" : "text-aura-text-muted hover:text-white"}`}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      <AffList affiliates={tab === "pending" ? pending : tab === "retail" ? retail : tab === "online" ? online : paused} onAction={doAction} onMarkMonthPaid={markMonthPaid} copyText={copyText} showQr={showQr} expandedId={expandedId} setExpandedId={setExpandedId} isPending={tab === "pending"} isPaused={tab === "paused"} />
    </>
  );
};

const AffList = ({ affiliates, onAction, onMarkMonthPaid, copyText, showQr, expandedId, setExpandedId, isPending, isPaused }) => {
  if (!affiliates.length) return <div className="card-aura p-8 text-center"><p className="text-aura-text-muted">No affiliates here.</p></div>;
  return <div className="space-y-3">{affiliates.map(a => <AffRow key={a.user_id} a={a} onAction={onAction} onMarkMonthPaid={onMarkMonthPaid} copyText={copyText} showQr={showQr} exp={expandedId === a.user_id} toggle={() => setExpandedId(expandedId === a.user_id ? null : a.user_id)} isPending={isPending} isPaused={isPaused} />)}</div>;
};

const AffRow = ({ a, onAction, onMarkMonthPaid, copyText, showQr, exp, toggle, isPending, isPaused }) => {
  const [showCust, setShowCust] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const cm = new Date().toISOString().slice(0, 7);

  const toggleDetails = async () => {
    const next = !showDetails;
    setShowDetails(next);
    if (next && !details && !loadingDetails) {
      setLoadingDetails(true);
      try {
        const res = await axios.get(`${API}/earn/admin/affiliates/${a.user_id}`, { headers });
        setDetails(res.data);
      } catch { toast.error("Failed to load details"); }
      setLoadingDetails(false);
    }
  };
  return (
    <div className="card-aura overflow-hidden">
      <div className="p-4 flex items-center justify-between flex-wrap gap-3 cursor-pointer" onClick={toggle}>
        <div className="flex items-center gap-3 min-w-0">
          <SB status={a.status} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-medium text-sm truncate">{a.name || "—"}</p>
              <UserCountBadge users={a.referred_users} testId={`user-count-badge-${a.user_id}`} />
            </div>
            <p className="text-xs text-aura-text-muted truncate">{a.email} | {a.affiliate_type}{a.business_type ? ` (${a.business_type})` : ""}{a.primary_platform ? ` (${a.primary_platform})` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-aura-text-muted hidden sm:inline">Refs:{a.total_referrals} W:{a.active_weekly} Y:{a.active_yearly} Due:${(a.monthly_due||0).toFixed(2)}</span>
          {a.referral_link && <Button size="sm" variant="outline" className="border-white/10 text-xs h-7" onClick={e => { e.stopPropagation(); copyText(a.referral_link); }}><Copy className="w-3 h-3 mr-1" />Link</Button>}
          {a.referral_link && <Button size="sm" variant="outline" className="border-white/10 text-xs h-7" onClick={e => { e.stopPropagation(); showQr(a.user_id); }}><QrCode className="w-3 h-3" /></Button>}
          {isPending && <><Button size="sm" className="bg-aura-emerald/20 text-aura-emerald text-xs h-7" onClick={e => { e.stopPropagation(); onAction(a.user_id, "approve"); }}>Approve</Button><Button size="sm" className="bg-red-500/20 text-red-400 text-xs h-7" onClick={e => { e.stopPropagation(); onAction(a.user_id, "reject"); }}>Reject</Button></>}
          {!isPending && !isPaused && a.status === "approved" && <Button size="sm" className="bg-yellow-500/20 text-yellow-400 text-xs h-7" onClick={e => { e.stopPropagation(); onAction(a.user_id, "pause"); }}><Pause className="w-3 h-3 mr-1" />Pause</Button>}
          {isPaused && <Button size="sm" className="bg-aura-emerald/20 text-aura-emerald text-xs h-7" onClick={e => { e.stopPropagation(); onAction(a.user_id, "unpause"); }}><Play className="w-3 h-3 mr-1" />Unpause</Button>}
          {!a.is_house && <Button size="sm" className="bg-red-500/10 text-red-400 text-xs h-7" onClick={e => { e.stopPropagation(); if (confirm("Delete this affiliate?")) onAction(a.user_id, "delete"); }}><Trash2 className="w-3 h-3" /></Button>}
          {exp ? <ChevronUp className="w-4 h-4 text-aura-text-muted" /> : <ChevronDown className="w-4 h-4 text-aura-text-muted" />}
        </div>
      </div>
      {exp && (
        <div className="border-t border-white/5 p-4 space-y-4 bg-white/[0.01]">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            <MS l="Pending" v={`$${(a.pending_commission||0).toFixed(2)}`} /><MS l="Locked" v={`$${(a.locked_commission||0).toFixed(2)}`} /><MS l="Eligible" v={`$${(a.eligible_commission||0).toFixed(2)}`} /><MS l="Earned" v={`$${(a.lifetime_earned||0).toFixed(2)}`} /><MS l="Paid" v={`$${(a.lifetime_paid||0).toFixed(2)}`} />
            <MS l="Weekly" v={a.active_weekly||0} /><MS l="Yearly" v={a.active_yearly||0} /><MS l="Cancelled" v={a.cancelled_total||0} />
          </div>
          {/* Application Details */}
          <div>
            <button onClick={toggleDetails} className="flex items-center gap-2 text-xs text-blue-400 hover:underline" data-testid={`details-toggle-${a.user_id}`}>{showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}Application Details</button>
            {showDetails && (
              <div className="mt-2 rounded-lg border border-white/5 bg-[#0a0e14] p-3 space-y-3" data-testid={`details-panel-${a.user_id}`}>
                {loadingDetails && <div className="flex items-center gap-2 text-xs text-aura-text-muted"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>}
                {details?.application && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <DR l="Affiliate Type" v={details.application.affiliate_type} />
                      <DR l="Status" v={details.application.status} />
                      <DR l="Email" v={details.application.email} />
                      <DR l="Mobile" v={details.application.mobile} />
                      <DR l="Applied" v={details.application.applied_at?.split("T")[0]} />
                      <DR l="Reviewed" v={details.application.reviewed_at?.split("T")[0]} />
                    </div>
                    {details.application.affiliate_type === "retail" && (
                      <div className="pt-2 border-t border-white/5">
                        <p className="text-xs text-aura-emerald uppercase tracking-wider mb-1">Retail Business</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <DR l="Business Name" v={details.application.business_name} />
                          <DR l="Contact Name" v={details.application.contact_name} />
                          <DR l="Business Type" v={details.application.business_type} />
                          <DR l="Store Address" v={details.application.store_address} full />
                          <DR l="Social Page URL" v={details.application.social_page_url} link full />
                          <DR l="Notes" v={details.application.retail_notes} full />
                        </div>
                      </div>
                    )}
                    {details.application.affiliate_type === "online" && (
                      <div className="pt-2 border-t border-white/5">
                        <p className="text-xs text-aura-emerald uppercase tracking-wider mb-1">Online Profile</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <DR l="First Name" v={details.application.first_name} />
                          <DR l="Last Name" v={details.application.last_name} />
                          <DR l="Primary Platform" v={details.application.primary_platform} />
                          <DR l="Follower Count" v={details.application.follower_count} />
                          <DR l="Main Profile URL" v={details.application.main_profile_url} link full />
                          <DR l="Secondary Profile URL" v={details.application.secondary_profile_url} link full />
                        </div>
                      </div>
                    )}
                    <div className="pt-2 border-t border-white/5">
                      <p className="text-xs text-aura-gold uppercase tracking-wider mb-1">Payout Details</p>
                      {details.payout_display?.method && details.payout_display.method !== "unknown" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <DR l="Method" v={details.payout_display.method === "bank_transfer" ? "Bank Transfer" : details.payout_display.method === "paypal" ? "PayPal" : details.payout_display.method} />
                          {details.payout_display.account_name && <DR l="Account Name" v={details.payout_display.account_name} />}
                          {details.payout_display.bsb && <DR l="BSB" v={details.payout_display.bsb} />}
                          {details.payout_display.account_number && <DR l="Account Number" v={details.payout_display.account_number} />}
                          {details.payout_display.paypal_email && <DR l="PayPal Email" v={details.payout_display.paypal_email} />}
                        </div>
                      ) : (
                        <p className="text-xs text-aura-text-muted">No payout details provided yet.</p>
                      )}
                    </div>
                  </>
                )}
                {!loadingDetails && !details?.application && <p className="text-xs text-aura-text-muted">No application data found.</p>}
              </div>
            )}
          </div>
          {/* Users */}
          <div>
            <button onClick={() => setShowCust(!showCust)} className="flex items-center gap-2 text-xs text-aura-emerald hover:underline" data-testid={`users-toggle-${a.user_id}`}>{showCust ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}Users ({a.referred_users?.length || 0})</button>
            {showCust && <UsersTable users={a.referred_users || []} isHouse={!!a.is_house} testId={`users-table-${a.user_id}`} />}
          </div>
          {/* Monthly Payouts */}
          <div>
            <button onClick={() => setShowPay(!showPay)} className="flex items-center gap-2 text-xs text-aura-gold hover:underline">{showPay ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}Monthly Payouts</button>
            {showPay && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                  <div><span className="text-xs text-white font-medium">{cm}</span><span className="text-xs text-aura-text-muted ml-2">Due: ${(a.eligible_commission||0).toFixed(2)}</span></div>
                  {(a.eligible_commission||0) > 0 && !a.is_house && <Button size="sm" className="bg-aura-gold/20 text-aura-gold text-xs h-6 px-2" onClick={() => onMarkMonthPaid(a.user_id, cm)}>Mark Paid</Button>}
                </div>
                {a.payouts?.map((p, i) => (<div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2"><span className="text-xs text-white">{p.month || p.created_at?.split("T")[0]} — ${(p.amount||0).toFixed(2)}</span><span className="text-xs px-2 py-0.5 rounded-full bg-aura-emerald/20 text-aura-emerald">Paid</span></div>))}
              </div>
            )}
          </div>
          {/* Email */}
          {a.email && <div className="flex items-center gap-2 pt-2 border-t border-white/5"><Button size="sm" variant="outline" className="border-white/10 text-xs h-7" onClick={() => { navigator.clipboard.writeText(a.email); toast.success("Email copied"); }}><Mail className="w-3 h-3 mr-1" /> Email</Button><span className="text-xs text-aura-text-muted">{a.email}</span></div>}
        </div>
      )}
    </div>
  );
};

/* ═══════════ USER EARN VIEW ═══════════ */
const UserEarnView = ({ user, token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  useEffect(() => { axios.get(`${API}/earn/affiliate/status`, { headers }).then(r => { setStatus(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);
  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-aura-emerald" /></div>;
  if (status?.status === "approved") return <ApprovedDash status={status} token={token} user={user} />;
  if (status?.status === "pending_review") return <div className="card-aura p-8 text-center"><Clock className="w-12 h-12 text-yellow-400 mx-auto mb-4" /><h2 className="font-heading text-xl font-bold text-white mb-2">Application Under Review</h2><p className="text-aura-text-secondary">Your affiliate application is being reviewed.</p></div>;
  return <AppForm token={token} onSuccess={(s) => setStatus({ status: s || "pending_review" })} />;
};

/* ═══════════ APPROVED AFFILIATE DASHBOARD ═══════════ */
const ApprovedDash = ({ status, token, user }) => {
  const { qrUrl, showQr, closeQr } = useQr(token);
  const [payoutData, setPayoutData] = useState(null);
  const [showPayForm, setShowPayForm] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied!"); };
  useEffect(() => { axios.get(`${API}/earn/affiliate/payout-details`, { headers }).then(r => setPayoutData(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-6">
      <QrModal qrUrl={qrUrl} onClose={closeQr} />
      <h1 className="font-heading text-2xl font-bold text-white">Your Affiliate Dashboard</h1>
      {/* Link + QR */}
      <div className="card-aura p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><p className="text-sm text-aura-text-secondary mb-1">Your Referral Link</p><p className="text-white font-mono text-sm break-all">{status.referral_link || "Generating..."}</p></div>
          <div className="flex gap-2">
            {status.referral_link && <Button size="sm" onClick={() => copyText(status.referral_link)}><Copy className="w-3 h-3 mr-1" /> Copy</Button>}
            <Button size="sm" variant="outline" className="border-white/10" onClick={() => showQr(user.user_id)}><QrCode className="w-3 h-3 mr-1" /> QR</Button>
          </div>
        </div>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SC label="Total Referred" value={status.total_referrals || 0} accent="emerald" />
        <SC label="Active" value={status.active_referrals || 0} />
        <SC label="Eligible Payout" value={`$${(status.eligible_commission || 0).toFixed(2)}`} accent="gold" />
        <SC label="Lifetime Earned" value={`$${(status.total_earned || 0).toFixed(2)}`} />
        <SC label="Lifetime Paid" value={`$${(status.total_paid || 0).toFixed(2)}`} />
        <SC label="Pending" value={`$${(status.pending_commission || 0).toFixed(2)}`} />
      </div>
      {/* Users (referred users) */}
      <div className="card-aura p-5">
        <button onClick={() => setShowUsers(s => !s)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="self-users-toggle">
          <h3 className="text-white font-medium text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-aura-emerald" />
            Users ({status.referred_users?.length || 0})
          </h3>
          {showUsers ? <ChevronUp className="w-4 h-4 text-aura-text-muted" /> : <ChevronDown className="w-4 h-4 text-aura-text-muted" />}
        </button>
        {showUsers && (
          <div className="mt-3">
            <UsersTable users={status.referred_users || []} isHouse={!!status.is_house} testId="self-users-table" />
          </div>
        )}
      </div>
      {/* Payout Details */}
      <div className="card-aura p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-medium text-sm">Payout Details</h3>
          <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => setShowPayForm(!showPayForm)}>{payoutData?.has_details ? "Update" : "Add"} Payout Details</Button>
        </div>
        {payoutData?.has_details && !showPayForm && (
          <div className="text-xs text-aura-text-muted space-y-1">
            <p>Method: <span className="text-white">{payoutData.payout_method === "bank_transfer" ? "Bank Transfer" : "PayPal"}</span></p>
            {payoutData.masked?.account_name && <p>Account: {payoutData.masked.account_name} | BSB: {payoutData.masked.bsb} | Acc: {payoutData.masked.account_number}</p>}
            {payoutData.masked?.paypal_email && <p>PayPal: {payoutData.masked.paypal_email}</p>}
          </div>
        )}
        {showPayForm && <PayoutForm token={token} onSaved={() => { setShowPayForm(false); axios.get(`${API}/earn/affiliate/payout-details`, { headers }).then(r => setPayoutData(r.data)); }} />}
        {!payoutData?.has_details && !showPayForm && <p className="text-xs text-aura-text-muted">No payout details yet. Add your bank or PayPal details to receive payouts.</p>}
      </div>
    </div>
  );
};

/* ═══════════ PAYOUT FORM ═══════════ */
const PayoutForm = ({ token, onSaved }) => {
  const [method, setMethod] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/earn/affiliate/payout-details`, { payout_method: method, ...form }, { headers });
      toast.success("Payout details saved");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };
  return (
    <form onSubmit={save} className="space-y-3 mt-3">
      <div className="space-y-1">
        <Label className="text-aura-text-secondary text-xs">Payout Method *</Label>
        <Select value={method} onValueChange={setMethod}><SelectTrigger className="input-aura"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="paypal">PayPal</SelectItem></SelectContent></Select>
      </div>
      {method === "bank_transfer" && (<div className="grid grid-cols-1 sm:grid-cols-3 gap-3"><Fld l="Account Name *" v={form.bank_account_name} fn={v => set("bank_account_name", v)} req /><Fld l="BSB *" v={form.bank_bsb} fn={v => set("bank_bsb", v)} req /><Fld l="Account Number *" v={form.bank_account_number} fn={v => set("bank_account_number", v)} req /></div>)}
      {method === "paypal" && <Fld l="PayPal Email *" v={form.paypal_email} fn={v => set("paypal_email", v)} t="email" req />}
      {method && <Button type="submit" size="sm" disabled={saving}>{saving ? "Saving..." : "Save Payout Details"}</Button>}
    </form>
  );
};

/* ═══════════ APPLICATION FORM ═══════════ */
const AppForm = ({ token, onSuccess }) => {
  const [type, setType] = useState("");
  const [form, setForm] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!type) { toast.error("Select affiliate type"); return; }
    if (!termsAccepted) { toast.error("You must accept the affiliate terms"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/earn/affiliate/apply`, { affiliate_type: type, ...form, agree_terms: true }, { headers });
      toast.success(res.data.status === "approved" ? "Approved instantly!" : "Application submitted!");
      onSuccess(res.data.status);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6">
      <div><h1 className="font-heading text-2xl font-bold text-white mb-2">Become an Affiliate</h1><p className="text-aura-text-secondary">Earn commissions by referring subscribers to AURA PRO.</p></div>
      <form onSubmit={submit} className="card-aura p-6 space-y-5">
        <div className="space-y-2"><Label className="text-aura-text-secondary">Affiliate Type *</Label><Select value={type} onValueChange={setType}><SelectTrigger className="input-aura"><SelectValue placeholder="Select type..." /></SelectTrigger><SelectContent><SelectItem value="retail">Retail Affiliate</SelectItem><SelectItem value="online">Online Affiliate</SelectItem></SelectContent></Select></div>
        {type === "retail" && (
          <div className="space-y-4 pt-2 border-t border-white/10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><Fld l="Business Name *" v={form.business_name} fn={v => set("business_name", v)} req /><Fld l="Contact Name *" v={form.contact_name} fn={v => set("contact_name", v)} req /><Fld l="Email *" v={form.email} fn={v => set("email", v)} t="email" req /><Fld l="Mobile *" v={form.mobile} fn={v => set("mobile", v)} req /></div>
            <div className="space-y-2"><Label className="text-aura-text-secondary text-xs">Business Type *</Label><Select value={form.business_type || ""} onValueChange={v => set("business_type", v)}><SelectTrigger className="input-aura"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="the_lott_retailer">The Lott Retailer</SelectItem><SelectItem value="convenience_store">Convenience Store / Milk Bar</SelectItem><SelectItem value="grocery_shop">Grocery Shop</SelectItem><SelectItem value="restaurant_cafe">Restaurant / Cafe</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            <Fld l="Store Address (optional)" v={form.store_address} fn={v => set("store_address", v)} />
            <Fld l="Social Page URL (optional)" v={form.social_page_url} fn={v => set("social_page_url", v)} />
            <Fld l="Notes (optional)" v={form.retail_notes} fn={v => set("retail_notes", v)} />
          </div>
        )}
        {type === "online" && (
          <div className="space-y-4 pt-2 border-t border-white/10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><Fld l="First Name *" v={form.first_name} fn={v => set("first_name", v)} req /><Fld l="Last Name *" v={form.last_name} fn={v => set("last_name", v)} req /><Fld l="Email *" v={form.email} fn={v => set("email", v)} t="email" req /><Fld l="Mobile *" v={form.mobile} fn={v => set("mobile", v)} req /></div>
            <div className="space-y-2"><Label className="text-aura-text-secondary text-xs">Primary Platform *</Label><Select value={form.primary_platform || ""} onValueChange={v => set("primary_platform", v)}><SelectTrigger className="input-aura"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="tiktok">TikTok</SelectItem><SelectItem value="instagram">Instagram</SelectItem><SelectItem value="facebook">Facebook</SelectItem><SelectItem value="youtube">YouTube</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><Fld l="Follower Count" v={form.follower_count} fn={v => set("follower_count", v)} /><Fld l="Main Profile URL *" v={form.main_profile_url} fn={v => set("main_profile_url", v)} req /></div>
            <Fld l="Secondary Profile URL (optional)" v={form.secondary_profile_url} fn={v => set("secondary_profile_url", v)} />
          </div>
        )}
        {type && (
          <>
            <div className="p-3 rounded-lg bg-white/5 border border-white/10"><p className="text-xs text-aura-text-muted">Payout details will only be requested after affiliate approval.</p></div>
            {/* Affiliate Terms */}
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-2">
              <p className="text-sm font-medium text-white">Affiliate Program Terms</p>
              <ul className="text-xs text-aura-text-secondary space-y-1 list-disc list-inside">
                <li>Earn <span className="text-white font-medium">30% commission</span> for every referred weekly subscriber</li>
                <li>Maximum earning per referred user: <span className="text-white font-medium">up to $54.60</span></li>
                <li>Commission applies for <span className="text-white font-medium">up to 12 months</span></li>
                <li>Payouts are made <span className="text-white font-medium">monthly</span></li>
                <li>Payout date: <span className="text-white font-medium">1st of each month</span> for the previous month</li>
                <li>If a referred subscriber cancels, future commission for that subscriber <span className="text-white font-medium">stops</span></li>
                <li>If that same subscriber subscribes again later, commission does <span className="text-white font-medium">not restart</span></li>
              </ul>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="mt-1 accent-[#00A86B] w-4 h-4" data-testid="terms-checkbox" />
              <span className="text-xs text-aura-text-secondary">I accept the AI Manifest affiliate terms and understand commissions are paid monthly for active weekly subscribers only.</span>
            </label>
            <Button type="submit" className="w-full btn-primary py-5" disabled={submitting || !termsAccepted}>{submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit Application"}</Button>
          </>
        )}
      </form>
    </div>
  );
};

/* ═══════════ SHARED TINY COMPONENTS ═══════════ */

/* Reusable Users table — used by admin (per-affiliate), affiliate self-view, and house card */
const UsersTable = ({ users, isHouse, testId }) => {
  if (!users || users.length === 0) {
    return <div className="mt-2 p-4 rounded-lg border border-white/5 bg-[#0a0e14] text-center text-xs text-aura-text-muted" data-testid={testId}>No users have joined via this link yet.</div>;
  }
  const deriveStatus = (u) => {
    if (u.status === "cancelled") return { label: "Cancelled", cls: "bg-red-500/20 text-red-400" };
    if (u.status === "active" && (u.paid_weeks || 0) > 0) return { label: u.plan_type === "yearly" ? "Paid Subscriber (Yearly)" : "Paid Subscriber", cls: "bg-aura-emerald/20 text-aura-emerald" };
    return { label: "Free User", cls: "bg-white/10 text-aura-text-muted" };
  };
  const remainingDays = (endDateIso) => {
    if (!endDateIso) return null;
    try {
      const end = new Date(endDateIso.split("T")[0]);
      const now = new Date();
      const ms = end - now;
      const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
      return days;
    } catch { return null; }
  };
  return (
    <div className="mt-2 overflow-x-auto max-h-64 overflow-y-auto rounded-lg border border-white/5" data-testid={testId}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-aura-text-muted border-b border-white/5 bg-[#0a0e14] sticky top-0">
            <th className="text-left py-1.5 px-2">User</th>
            <th className="text-center py-1.5 px-2">Status</th>
            <th className="text-left py-1.5 px-2">Joined</th>
            <th className="text-left py-1.5 px-2">Paid Start</th>
            <th className="text-left py-1.5 px-2">Comm. End</th>
            <th className="text-center py-1.5 px-2">Remaining</th>
            <th className="text-left py-1.5 px-2">Cancelled</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => {
            const s = deriveStatus(u);
            const isCancelled = u.status === "cancelled";
            // Gray out only for normal affiliates. House affiliate shows cancelled without gray-out.
            const grayOut = isCancelled && !isHouse;
            const remDays = remainingDays(u.commission_end_date);
            const remText = remDays === null ? "—" : (remDays > 0 ? `${remDays}d left` : "Expired");
            return (
              <tr key={i} className={`border-b border-white/5 ${grayOut ? "opacity-40" : ""}`} data-testid={`user-row-${i}`}>
                <td className="py-1 px-2 text-white truncate max-w-[220px]">{u.email || u.masked_user || u.name || "—"}</td>
                <td className="py-1 px-2 text-center">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                </td>
                <td className="py-1 px-2 text-aura-text-muted">{u.signup_date?.split("T")[0] || "—"}</td>
                <td className="py-1 px-2 text-aura-text-muted">{u.paid_start_date?.split("T")[0] || "—"}</td>
                <td className="py-1 px-2 text-aura-text-muted">{isHouse ? "—" : (u.commission_end_date?.split("T")[0] || "—")}</td>
                <td className="py-1 px-2 text-center text-aura-text-muted">{isHouse ? "—" : remText}</td>
                <td className="py-1 px-2 text-aura-text-muted">{u.cancellation_date?.split("T")[0] || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Cancelled subscriber footer note (only for non-house) */}
      {!isHouse && users.some(u => u.status === "cancelled" && (u.paid_weeks || 0) > 0) && (
        <div className="px-3 py-2 bg-[#0a0e14] border-t border-white/5 text-[11px] text-red-400" data-testid="cancelled-subscriber-note">
          Subscriber cancelled — no future payouts
        </div>
      )}
    </div>
  );
};

/* House / Admin Affiliate card — pinned at top of admin Earn tab, with expandable Users dropdown */
const HouseAffiliateCard = ({ house, copyText, showQr }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-aura p-4 border-aura-emerald/30 bg-gradient-to-r from-aura-emerald/5 to-transparent" data-testid="house-affiliate-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-aura-emerald/20 flex items-center justify-center"><Star className="w-4 h-4 text-aura-emerald" /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-medium text-sm">House / Admin Affiliate <span className="text-xs text-aura-emerald ml-1">Internal — No Payout</span></p>
              <UserCountBadge users={house.referred_users} testId="user-count-badge-house" />
            </div>
            <p className="text-xs text-aura-text-muted truncate">{house.referral_link || "No link"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {house.referral_link && <Button size="sm" variant="outline" className="border-white/10 text-xs h-7" onClick={() => copyText(house.referral_link)}><Copy className="w-3 h-3 mr-1" /> Link</Button>}
          <Button size="sm" variant="outline" className="border-white/10 text-xs h-7" onClick={() => showQr(house.user_id)}><QrCode className="w-3 h-3 mr-1" /> QR</Button>
          <span className="text-xs text-aura-text-muted">Refs:{house.total_referrals} W:{house.active_weekly} Y:{house.active_yearly}</span>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-white/5">
        <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 text-xs text-aura-emerald hover:underline" data-testid="house-users-toggle">
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Users ({house.referred_users?.length || 0})
        </button>
        {open && <UsersTable users={house.referred_users || []} isHouse={true} testId="house-users-table" />}
      </div>
    </div>
  );
};

const Fld = ({ l, v, fn, t = "text", req }) => (<div className="space-y-1"><Label className="text-aura-text-secondary text-xs">{l}</Label><Input type={t} value={v || ""} onChange={e => fn(e.target.value)} className="input-aura" required={req} /></div>);

/* Compact user-count badge — paid · free · cancelled */
const UserCountBadge = ({ users, testId }) => {
  const list = users || [];
  const paid = list.filter(u => u.status === "active" && (u.paid_weeks || 0) > 0).length;
  const cancelled = list.filter(u => u.status === "cancelled").length;
  const free = list.length - paid - cancelled;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-aura-text-muted whitespace-nowrap"
      title="Paid · Free · Cancelled users referred"
      data-testid={testId}
    >
      <span className="text-aura-emerald">{paid} paid</span>
      <span className="text-white/30">·</span>
      <span>{free} free</span>
      <span className="text-white/30">·</span>
      <span className="text-red-400/80">{cancelled} cancelled</span>
    </span>
  );
};
const DR = ({ l, v, link, full }) => {
  const value = v || "—";
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <span className="text-aura-text-muted">{l}: </span>
      {link && v ? (
        <a href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-aura-emerald hover:underline break-all">{value}</a>
      ) : (
        <span className="text-white break-all">{value}</span>
      )}
    </div>
  );
};
const SC = ({ label, value, accent }) => { const c = { emerald: "text-aura-emerald", gold: "text-aura-gold", yellow: "text-yellow-400", blue: "text-blue-400", purple: "text-purple-400" }; return <div className="card-aura p-3 text-center"><p className={`text-xl font-bold ${c[accent] || "text-white"}`}>{value}</p><p className="text-xs text-aura-text-muted">{label}</p></div>; };
const MS = ({ l, v }) => <div className="bg-white/5 rounded-lg p-2 text-center"><p className="text-sm font-semibold text-white">{v}</p><p className="text-[10px] text-aura-text-muted">{l}</p></div>;
const SB = ({ status }) => { const s = { pending_review: "bg-yellow-500/20 text-yellow-400", approved: "bg-aura-emerald/20 text-aura-emerald", rejected: "bg-red-500/20 text-red-400", paused: "bg-white/10 text-aura-text-muted", deleted: "bg-red-500/10 text-red-300" }; return <span className={`text-xs px-2 py-1 rounded-full ${s[status] || "bg-white/5 text-aura-text-muted"}`}>{(status || "").replace("_", " ")}</span>; };

export default EarnPage;
